import React, { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CSSTransition } from "react-transition-group";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useParams } from "react-router-dom";
import { onBoardingFlow } from "../index";
import { network } from "../../../apis";
import { LARAVEL_APP_HOST, STORAGE } from "../../../constants";
import { User } from "../../../models/User";
import { AuthContext } from "AuthContextWrapper";

import clipboardImg1x from "../../assets/ani-clipboard-1x.png";
import clipboardImg15x from "../../assets/ani-clipboard-1.5x.png";
import clipboardImg2x from "../../assets/ani-clipboard-2x.png";
import clipboardImg3x from "../../assets/ani-clipboard-3x.png";
import scaleImg1x from "../../assets/ani-scale-1x.png";
import scaleImg15x from "../../assets/ani-scale-1.5x.png";
import scaleImg2x from "../../assets/ani-scale-2x.png";
import scaleImg3x from "../../assets/ani-scale-3x.png";
import clipboardTwo1x from "../../assets/ani-clipboard2-1x.png";
import clipboardTwo15x from "../../assets/ani-clipboard2-1.5x.png";
import clipboardTwo2x from "../../assets/ani-clipboard2-2x.png";
import clipboardTwo3x from "../../assets/ani-clipboard2-3x.png";
import checkBall1x from "../../assets/ani-checkball-1x.png";
import checkBall15x from "../../assets/ani-checkball-1.5x.png";
import checkBall2x from "../../assets/ani-checkball-2x.png";
import checkBall3x from "../../assets/ani-checkball-3x.png";

import "./animations.css";
import { Estate } from "models/Estate";

const bulletItemStyle = {
  display: "flex",
  alignItems: "center",
  width: {
    xs: "90%",
    sm: "80%",
    md: "60%",
  },
  mx: "auto",
  my: 2,
  textAlign: "left",
  "& img": {
    display: "inline-block",
    marginRight: 1,
    verticalAlign: "middle",
  },
} as const;

const animatedElement = {
  position: "absolute",
  textAlign: "center",
  mx: "auto",
  left: 0,
  right: 0,
} as const;

const animatedIcon = {
  height: "120px",
};

// Animation use react-transition-group library
//  https://reactcommunity.org/react-transition-group/css-transition
//  https://www.npmjs.com/package/react-transition-group
export const AnimateYourPlanScreen = (): JSX.Element => {
  const { screenName } = useParams();
  const { activeEstate } = useContext(AuthContext);
  const currentScreen = onBoardingFlow.getCurrentFlowScreen(screenName);

  // animation trackers
  const [scene1Icon, setScene1Icon] = useState(false);
  const [scene1Title, setScene1Title] = useState(false);
  const [scene2Icon, setScene2Icon] = useState(false);
  const [scene2Title, setScene2Title] = useState(false);
  const [scene2Bullet1, setScene2Bullet1] = useState(false);
  const [scene2Bullet2, setScene2Bullet2] = useState(false);
  const [scene3Icon, setScene3Icon] = useState(false);
  const [scene3Title, setScene3Title] = useState(false);

  const handleComplete = async (
    saveComplete = true,
    redirect = true
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (saveComplete) {
      // mark on-boarding complete
      await network.callPut(`${User.path}/me/complete-on-boarding`);
      // if this user had selected a first free form in the forms app we will add it here
      const firstFreeFormId = window.sessionStorage.getItem(
        STORAGE.firstFreeFormId
      );
      if (firstFreeFormId && activeEstate) {
        console.log("Attaching first free form id=" + firstFreeFormId);
        await network.callPost(
          `${Estate.path}/${activeEstate.id}/forms/${firstFreeFormId}`
        );
      }
    }

    if (redirect) {
      window.location.href = `${LARAVEL_APP_HOST}/payment-plan`;
    }
    return true;
  };

  useEffect(() => {
    setTimeout(() => {
      // start the animation sequence
      setScene1Icon(true);
    }, 500);
  }, []);

  // use 0.5 of screens larger than 1000, 0.8 for screens smaller than that
  const stageHeight =
    window.innerHeight -
    window.innerHeight * (window.innerHeight > 1000 ? 0.5 : 0.2);

  return currentScreen ? (
    <FlowScreenWrapper hideSectionHeading={true} centerContent={true}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: stageHeight,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* clipboard icon */}
          <CSSTransition
            in={scene1Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene1Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={clipboardImg1x}
                srcSet={`${clipboardImg15x} 1.5x, ${clipboardImg2x} 2x, ${clipboardImg3x} 3x`}
                alt="clipboard"
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene1Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // slide both out icon and title
                setScene1Icon(false);
                setScene1Title(false);
              }, 3000);
            }}
            onExited={() => {
              // slide in the next icon
              setScene2Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">Reviewing your answers...</Typography>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* scale icon */}
          <CSSTransition
            in={scene2Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene2Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={scaleImg1x}
                srcSet={`${scaleImg15x} 1.5x, ${scaleImg2x} 2x, ${scaleImg3x} 3x`}
                alt="scale"
                style={{
                  display: "inline-block",
                  marginTop: "30px",
                }}
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene2Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // fade in first bullet
                setScene2Bullet1(true);
              }, 500);
            }}
            onExited={() => {
              setScene3Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography
                variant="h3"
                sx={{
                  mb: 3,
                }}
              >
                Determining the type of probate required...
              </Typography>
              <CSSTransition
                in={scene2Bullet1}
                timeout={500}
                classNames="bullet"
                unmountOnExit
                onEntered={() => {
                  setTimeout(() => {
                    // fade in the next bullet
                    setScene2Bullet2(true);
                  }, 500);
                }}
              >
                <Box sx={bulletItemStyle}>
                  <img
                    src={checkBall1x}
                    srcSet={`${checkBall15x} 1.5x, ${checkBall2x} 2x, ${checkBall3x} 3x`}
                    alt="check"
                  />
                  <Typography variant="body1">
                    Reviewing your qualifications
                  </Typography>
                </Box>
              </CSSTransition>
              <CSSTransition
                in={scene2Bullet2}
                timeout={500}
                classNames="bullet"
                unmountOnExit
                onEntered={() => {
                  setTimeout(() => {
                    // slide both out icon and title
                    //  & fade bullets
                    setScene2Icon(false);
                    setScene2Title(false);
                    setScene2Bullet1(false);
                    setScene2Bullet2(false);
                  }, 3000);
                }}
              >
                <Box sx={bulletItemStyle}>
                  <img
                    src={checkBall1x}
                    srcSet={`${checkBall15x} 1.5x, ${checkBall2x} 2x, ${checkBall3x} 3x`}
                    alt="check"
                  />
                  <Typography variant="body1">
                    Reviewing the value of the estate’s assets
                  </Typography>
                </Box>
              </CSSTransition>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* clipboard2 icon */}
          <CSSTransition
            in={scene3Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene3Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={clipboardTwo1x}
                srcSet={`${clipboardTwo15x} 1.5x, ${clipboardTwo2x} 2x, ${clipboardTwo3x} 3x`}
                alt="clipboard"
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene3Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // slide both out icon and title
                setScene3Icon(false);
                setScene3Title(false);
              }, 4000);
            }}
            onExited={() => {
              // handleComplete(false, false); // swap for this one to make debugging easier
              handleComplete();
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">
                Preparing your personalized estate settlement guide...
              </Typography>
            </Box>
          </CSSTransition>
        </Box>
      </Box>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
